import { ConnectionEnvironment } from '@/enums';
import { getAccessToken } from '@/lib/tokenStorage';

export type Workflow = {
  id: string;
  label: string;
  entityName?: string;
};

export type WorkflowValidation = (workflow: Workflow) =>
  | {
      isValid: boolean;
      message?: string;
    }
  | Promise<{
      isValid: boolean;
      message?: string;
    }>;

export const WORKFLOW = {
  Rollover2Week: {
    id: '1733D1C8-C3E2-430B-A308-AE65DAB9A697',
    name: 'Rollover2Week',
    label: '2 Week Rollover',
    entityName: 'DonorSchedule',
  },
  Rollover4Week: {
    id: 'EA60F6EB-5A8F-45F9-B21C-E63A1C4D1760',
    name: 'Rollover4Week',
    label: '4 Week Rollover',
    entityName: 'DonorSchedule',
  },
  SOSchedule: {
    id: 'E915E48C-1621-410F-A445-3736982CE29F',
    name: 'SOSchedule',
    label: 'SO Schedule',
    entityName: 'DonorSchedule',
  },
  Split: {
    id: '16ED0F40-E709-4818-949F-9FFC118FD748',
    name: 'Split',
    label: 'Split',
    entityName: 'DonorSchedule',
  },
  DonorProcedure: {
    id: '12519CF4-E5E6-43CB-94CD-41C882106FFC',
    name: 'DonorProcedureAspResults',
    label: 'Donor Procedure',
    entityName: 'Fertilization',
  },
  Ship: {
    id: '08C33946-A443-4B45-8D5D-934A6B7BC717',
    name: 'Ship',
    label: 'Ship',
    entityName: 'Shipping',
  },
  ShipToIntra: {
    id: '494F6C2D-9B34-4DA0-88F6-D2CAC529F388',
    name: 'ShipToIntra',
    label: 'Ship to INTRA',
    entityName: 'Shipping',
  },

  RemoveFromShipment: {
    id: '8AC6DADD-21B1-4E7F-BFB5-74580068BB78',
    name: 'RemoveFromShipment',
    label: 'Remove from Shipment',
    entityName: 'Shipping',
  },

  CreateEmbryoImplant: {
    id: 'B7A3C1E0-4D5F-4A8B-9C2E-1F6D3A7B8E90',
    name: 'CreateEmbryoImplant',
    label: 'Create Embryo (Implant)',
    entityName: 'EmbryoImplantEntry',
  },
  CreateEmbryoFreeze: {
    id: 'D4E8F2A1-6B9C-4D7E-8A1F-3C5E7D9B2A40',
    name: 'CreateEmbryoFreeze',
    label: 'Create Embryo (Freeze)',
    entityName: 'EmbryoFreezeEntry',
  },
};

export const runWorkflow = async (
  environmentId: number,
  workflowId: string,
  recordIds: Array<{ RecordId: string }> = [],
) => {
  try {
    environmentId = environmentId ?? ConnectionEnvironment.TRAINING;
    const json = {
      EnvironmentId: environmentId,
      WorkFlowId: workflowId,
      RecordIds: recordIds,
    };
    const encodedString = btoa(JSON.stringify(json));

    const res = await fetch(
      `${import.meta.env.VITE_WORKFLOW_API}?blob=${encodedString}`,
    );

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(errorText || `HTTP Error: ${res.status}`);
    }

    const responseData = await res.text();

    return {
      success: true,
      status: res.status,
      statusText: res.statusText,
      data: responseData,
      ok: res.ok,
    };
  } catch (err) {
    throw err instanceof Error ? err : new Error(`Unknown Error: ${err}`);
  }
};

export const runNewWorkflow = async (
  Ids: Array<{ RecordId: string }> = [],
  workflowId: string,
) => {
  try {
    let workflowName = '';
    let entityName = '';
    switch (workflowId) {
      case WORKFLOW.Rollover2Week.id:
        workflowName = WORKFLOW.Rollover2Week.name;
        entityName = WORKFLOW.Rollover2Week.entityName;
        break;
      case WORKFLOW.Rollover4Week.id:
        workflowName = WORKFLOW.Rollover4Week.name;
        entityName = WORKFLOW.Rollover4Week.entityName;
        break;
      case WORKFLOW.SOSchedule.id:
        workflowName = WORKFLOW.SOSchedule.name;
        entityName = WORKFLOW.SOSchedule.entityName;
        break;
      case WORKFLOW.Split.id:
        workflowName = WORKFLOW.Split.name;
        entityName = WORKFLOW.Split.entityName;
        break;
      case WORKFLOW.DonorProcedure.id:
        workflowName = WORKFLOW.DonorProcedure.name;
        entityName = WORKFLOW.DonorProcedure.entityName;
        break;
      case WORKFLOW.RemoveFromShipment.id:
        workflowName = WORKFLOW.RemoveFromShipment.name;
        entityName = WORKFLOW.RemoveFromShipment.entityName;
        break;
      case WORKFLOW.CreateEmbryoImplant.id:
        workflowName = WORKFLOW.CreateEmbryoImplant.name;
        entityName = WORKFLOW.CreateEmbryoImplant.entityName;
        break;
      case WORKFLOW.CreateEmbryoFreeze.id:
        workflowName = WORKFLOW.CreateEmbryoFreeze.name;
        entityName = WORKFLOW.CreateEmbryoFreeze.entityName;
        break;
      default:
        workflowName = '';
        entityName = '';
        break;
    }
    const json = {
      Ids: (Ids ?? [])
        .map((x) => x?.RecordId)
        .filter((id) => typeof id === 'string' && id.length > 0),
      Name: workflowName,
      WorkflowGuid: workflowId,
      EntityName: entityName,
    };

    const token = getAccessToken();
    if (!token) {
      throw new Error('No access token available. Please log in.');
    }

    const url = `${import.meta.env.VITE_API_BASE_URL}/api/workflow`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(json),
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(errorText || `HTTP Error: ${res.status}`);
    }

    const responseData = await res.text();

    return {
      success: true,
      status: res.status,
      statusText: res.statusText,
      data: responseData,
      ok: res.ok,
    };
  } catch (err) {
    throw err instanceof Error ? err : new Error(`Unknown Error: ${err}`);
  }
};
